<template>
    <div class="jetty-form-page">
        <div class="top-bar">
            <div class="top-left">
                <div class="shift-chip" :class="shiftType === 'NIGHT' ? 'night' : 'day'">
                    {{ shiftLabel }} Shift
                </div>
                <div class="jetty-label">Jetty {{ jettyNo }}</div>
            </div>

            <div class="top-right">
                <button class="ghost-btn" @click="showTimeline = !showTimeline">
                    <i class="pi pi-directions-alt mr-2"></i>
                    {{ showTimeline ? 'Hide Timeline' : 'Show Timeline' }}
                </button>

                <button class="primary-btn" :disabled="isViewOnly || saving || isAnyUploadingPictures || pageLoading"
                    @click="handleSave">
                    {{ saving ? 'Saving...' : 'Save' }}
                </button>

                <button class="secondary-btn" @click="goBack">
                    Close
                </button>
            </div>
        </div>

        <div class="page-body" :class="{ 'timeline-hidden': !showTimeline }">
            <div class="form-panel">
                <div class="form-scroll">
                    <div class="date-row">
                        <div>
                            <label class="mr-2">Date:</label>
                            <input type="text" :value="displayDate" disabled class="top-date-input" />
                        </div>

                        <div class="flex gap-2">
                            <button v-if="!(isViewOnly || pageLoading)" type="button" class="add-entry-btn"
                                @click="addEntry" :disabled="pageLoading">
                                + Add Entry
                            </button>

                            <button v-if="!(isViewOnly || pageLoading)" type="button" class="add-entry-btn"
                                @click="retrievePreviousShiftLastEntry">
                                Retrieve Previous Shift
                            </button>
                        </div>
                    </div>

                    <div class="entry-tabs-row">
                        <div class="entry-tabs">
                            <div v-for="(entry, index) in entries" :key="entry.localId" class="entry-tab"
                                :class="{ active: activeEntryIndex === index }" @click="setActiveEntry(index)">
                                <span>Entry {{ index + 1 }}</span>

                                <button v-if="!isViewOnly && entries.length > 1" type="button" class="remove-entry-btn"
                                    @click.stop="removeEntry(index)">
                                    ×
                                </button>
                            </div>
                        </div>

                    </div>

                    <div class="section-title">
                        <span class="section-bar"></span>
                        <span>Shift - {{ shiftLabel }}</span>
                    </div>

                    <table class="form-table">
                        <tr>
                            <td class="field-label">Link to Nomination</td>
                            <td>
                                <div class="nomination-row">
                                    <el-select v-model="activeForm.nominationId" placeholder="Select nomination"
                                        filterable clearable :disabled="isViewOnly || pageLoading"
                                        class="el-full-control" @change="onNominationChange">
                                        <el-option v-for="item in nominationOptions" :key="item.id"
                                            :label="`${item.nominationNo || item.nominationId || item.id} - ${item.vesselName || ''}`"
                                            :value="item.id" />
                                    </el-select>

                                    <button v-if="!isViewOnly || pageLoading" type="button" class="ghost-btn w-1/4"
                                        @click="reExtractNomination">
                                        Re-extract
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </table>

                    <table class="form-table">
                        <tr>
                            <td class="field-label required">Time Taken Over</td>
                            <td>
                                <el-time-picker v-model="activeForm.timeTakenOver" format="HH:mm" value-format="HH:mm"
                                    placeholder="Select time" :disabled="isViewOnly || pageLoading"
                                    class="el-full-control"
                                    :class="{ 'error-border': validationErrors.timeTakenOver }" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Team No.</td>
                            <td>
                                <select v-model="activeForm.teamNo" :disabled="isViewOnly || pageLoading">
                                    <!-- <option value="">Select Team</option> -->
                                    <option v-for="team in teamOptions" :key="team.teamId" :value="team.value">
                                        {{ team.label }}
                                    </option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Vessel's Name</td>
                            <td><input type="text" v-model="activeForm.vesselName"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">LOA / Flag</td>
                            <td><input type="text" v-model="activeForm.loaFlag" :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Captain's Name</td>
                            <td><input type="text" v-model="activeForm.captainName"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Draft</td>
                            <td>
                                <div class="inline-grid two-cols">
                                    <div class="mini-field">
                                        <label>Before (F):</label>
                                        <input type="text" v-model="activeForm.draftBeforeF"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>

                                    <div class="mini-field">
                                        <label>Before (A):</label>
                                        <input type="text" v-model="activeForm.draftBeforeA"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>

                                    <div class="mini-field">
                                        <label>After (F):</label>
                                        <input type="text" v-model="activeForm.draftAfterF"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>

                                    <div class="mini-field">
                                        <label>After (A):</label>
                                        <input type="text" v-model="activeForm.draftAfterA"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Freeboard</td>
                            <td>
                                <div class="inline-grid one-col">
                                    <div class="mini-field">
                                        <label>Before:</label>
                                        <input type="text" v-model="activeForm.freeboardBefore"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>
                                    <div class="mini-field">
                                        <label>After:</label>
                                        <input type="text" v-model="activeForm.freeboardAfter"
                                            :disabled="isViewOnly || pageLoading" />
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Size & No. of Manifold</td>
                            <td><input type="text" v-model="activeForm.manifold"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label" :class="{ required: showSealFields }">Hose No.</td>
                            <td>
                                <input type="text" v-model="activeForm.hoseNo" :disabled="isViewOnly || pageLoading"
                                    :class="{ 'error-border': validationErrors.hoseNo }" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">ROB (Tank(s) & levels)</td>
                            <td><input type="text" v-model="activeForm.robTanks"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">MPA Water Voucher No.</td>
                            <td><input type="text" v-model="activeForm.mpaVoucherNo"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Totaliser Opening Reading</td>
                            <td><input type="text" v-model="activeForm.totaliserOpening"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Totaliser Closing Reading</td>
                            <td><input type="text" v-model="activeForm.totaliserClosing"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                    </table>

                    <div class="subsection-header subsection-header-action">
                        <span>Condition of Jetty</span>

                        <button v-if="!isViewOnly" type="button" class="check-all-btn"
                            @click="toggleAllConditionStatus">
                            {{ allConditionChecked ? 'Uncheck All' : 'Check All' }}
                        </button>
                    </div>
                    <table class="form-table condition-table">
                        <tr class="table-head-row">
                            <td class="field-label">Equipment</td>
                            <td class="status-col">Status</td>
                            <td>Remarks</td>
                        </tr>

                        <tr v-for="(item, index) in activeConditionItems" :key="index">
                            <td class="field-label required">{{ item.label }}</td>

                            <template v-if="item.type !== 'select'">
                                <td class="status-col">
                                    <input type="checkbox" v-model="item.checked"
                                        :disabled="isViewOnly || pageLoading" />
                                </td>
                                <td>
                                    <input type="text" v-model="item.remarks" :disabled="isViewOnly || pageLoading" />
                                </td>
                            </template>

                            <template v-else>
                                <td colspan="2">
                                    <select v-model="item.level" :disabled="isViewOnly || pageLoading">
                                        <option value="">Select Type</option>

                                        <template v-if="item.label === 'Sump Pump Level'">
                                            <option value="LOW">Low</option>
                                            <option value="HIGH">High</option>
                                        </template>

                                        <template v-else>
                                            <option value="ON">On</option>
                                            <option value="OFF">Off</option>
                                            <option value="MAINTENANCE">Maintenance</option>
                                        </template>
                                    </select>
                                </td>
                            </template>
                        </tr>
                        <tr>
                            <td class="field-label required">
                                Jetty Overview Photo
                            </td>
                            <td colspan="2">
                                <div v-if="!isViewOnly" class="upload-box" @click="triggerFileInputByType(1)"
                                    :class="{ 'error-border': validationErrors.jettyOverviewPhoto }">
                                    <input ref="overviewPictureInput" type="file" accept="image/*" multiple
                                        class="hidden-file-input" @change="handlePictureChangeByType($event, 1)"
                                        :disabled="isViewOnly || pageLoading || uploadingOverviewPictures || getPicturesByType(1).length >= 2" />
                                    <i class="pi pi-upload"></i>
                                    <span>
                                        {{ uploadingOverviewPictures ? 'Uploading...' : 'Upload Photo' }}
                                    </span>
                                </div>

                                <div v-if="getPicturesByType(1).length" class="uploaded-picture-list">
                                    <div v-for="(picture, index) in getPicturesByType(1)"
                                        :key="picture.pictureUrl + index" class="uploaded-picture-item">
                                        <div class="uploaded-picture-left">
                                            <button type="button" class="picture-link-btn"
                                                @click="openPhotoDialog(picture.pictureUrl)">
                                                {{ getPictureName(picture.pictureUrl, index) }}
                                            </button>
                                        </div>

                                        <div class="uploaded-picture-actions">
                                            <button v-if="!isViewOnly" type="button" class="remove-picture-btn"
                                                @click="removePictureByType(1, index)">
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <tr>
                            <td class="field-label">
                                Faulty Equipment Photo
                            </td>
                            <td colspan="2">
                                <div v-if="!isViewOnly" class="upload-box" @click="triggerFileInputByType(2)">
                                    <input ref="faultyPictureInput" type="file" accept="image/*" multiple
                                        class="hidden-file-input" @change="handlePictureChangeByType($event, 2)"
                                        :disabled="isViewOnly || pageLoading || uploadingFaultyPictures || getPicturesByType(2).length >= 5" />
                                    <i class="pi pi-upload"></i>
                                    <span>
                                        {{ uploadingFaultyPictures ? 'Uploading...' : 'Upload Photo' }}
                                    </span>
                                </div>

                                <div v-if="getPicturesByType(2).length" class="uploaded-picture-list">
                                    <div v-for="(picture, index) in getPicturesByType(2)"
                                        :key="picture.pictureUrl + index" class="uploaded-picture-item">
                                        <div class="uploaded-picture-left">
                                            <button type="button" class="picture-link-btn"
                                                @click="openPhotoDialog(picture.pictureUrl)">
                                                {{ getPictureName(picture.pictureUrl, index) }}
                                            </button>
                                        </div>

                                        <div class="uploaded-picture-actions">
                                            <button v-if="!isViewOnly" type="button" class="remove-picture-btn"
                                                @click="removePictureByType(2, index)">
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <tr>
                            <td class="field-label">Faulty Equipment Remarks</td>
                            <td colspan="2">
                                <input type="text" v-model="activeForm.faultyEquipmentRemarks"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>

                        <tr>
                            <td class="field-label">
                                Bypass Seal Photo
                            </td>
                            <td colspan="2">
                                <div v-if="!isViewOnly" class="upload-box" @click="triggerFileInputByType(3)">
                                    <input ref="bypassPictureInput" type="file" accept="image/*" multiple
                                        class="hidden-file-input" @change="handlePictureChangeByType($event, 3)"
                                        :disabled="isViewOnly || pageLoading || uploadingBypassPictures || getPicturesByType(3).length >= 2" />
                                    <i class="pi pi-upload"></i>
                                    <span>
                                        {{ uploadingBypassPictures ? 'Uploading...' : 'Upload Photo' }}
                                    </span>
                                </div>

                                <div v-if="getPicturesByType(3).length" class="uploaded-picture-list">
                                    <div v-for="(picture, index) in getPicturesByType(3)"
                                        :key="picture.pictureUrl + index" class="uploaded-picture-item">
                                        <div class="uploaded-picture-left">
                                            <button type="button" class="picture-link-btn"
                                                @click="openPhotoDialog(picture.pictureUrl)">
                                                {{ getPictureName(picture.pictureUrl, index) }}
                                            </button>
                                        </div>

                                        <div class="uploaded-picture-actions">
                                            <button v-if="!isViewOnly" type="button" class="remove-picture-btn"
                                                @click="removePictureByType(3, index)">
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <tr v-if="hasBypassSealPhotos">
                            <td class="field-label required">Bypass Seal</td>
                            <td colspan="2">
                                <select v-model="activeForm.bypassSeal" :disabled="isViewOnly || pageLoading"
                                    :class="{ 'error-border': validationErrors.bypassSeal }">
                                    <option value="Break Seal">Break Seal</option>
                                    <option value="Lock Seal">Lock Seal</option>
                                    <option value="Area Check">Area Check</option>
                                </select>
                            </td>
                        </tr>

                        <tr v-if="showSealFields">
                            <td class="field-label required">Seal Remarks</td>
                            <td colspan="2">
                                <input type="text" v-model="activeForm.sealRemarks"
                                    :disabled="isViewOnly || pageLoading"
                                    :class="{ 'error-border': validationErrors.sealRemarks }" />
                            </td>
                        </tr>

                        <tr v-if="showSealFields">
                            <td class="field-label required">Seal Number</td>
                            <td colspan="2">
                                <input type="text" v-model="activeForm.sealNumber" :disabled="isViewOnly || pageLoading"
                                    :class="{ 'error-border': validationErrors.sealNumber }" />
                            </td>
                        </tr>
                    </table>

                    <div class="subsection-header">Operations Information</div>
                    <table class="form-table">
                        <tr>
                            <td class="field-label">Ship's Requested Loading Rate</td>
                            <td><input type="text" v-model="activeForm.requestedLoadingRate"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Ship's Max. Discharging Rate</td>
                            <td><input type="text" v-model="activeForm.maxDischargingRate"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Line Drip Sample</td>
                            <td>
                                <select v-model="activeForm.lineDripSample" :disabled="isViewOnly || pageLoading">
                                    <!-- <option value="">Select</option>  -->
                                    <option value="I/P">I/P</option>
                                    <option value="Taken">Taken</option>
                                    <option value="To follow up">To follow up</option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Ship's Composite / Load Port Sample</td>
                            <td><input type="text" v-model="activeForm.compositeSample"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Product's Name</td>
                            <td><input type="text" v-model="activeForm.productName"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Density</td>
                            <td><input type="text" v-model="activeForm.density" :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Temperature</td>
                            <td><input type="text" v-model="activeForm.temperature"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Ship's Nomination</td>
                            <td><input type="text" v-model="activeForm.shipNomination"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Shore's Nomination</td>
                            <td><input type="text" v-model="activeForm.shoreNomination"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Customer</td>
                            <td><input type="text" v-model="activeForm.customer"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Surveyor</td>
                            <td><input type="text" v-model="activeForm.surveyor"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Pilot Arrangement</td>
                            <td>
                                <div class="radio-row">
                                    <label><input type="radio" value="IHP" v-model="activeForm.pilotArrangement"
                                            :disabled="isViewOnly || pageLoading" />
                                        IHP</label>
                                    <label><input type="radio" value="PSA" v-model="activeForm.pilotArrangement"
                                            :disabled="isViewOnly || pageLoading" />
                                        PSA</label>
                                </div>
                            </td>
                        </tr>
                    </table>

                    <div class="subsection-header">Activities</div>
                    <table class="form-table">
                        <tr>
                            <td class="field-label">Loading / Discharging (Shore Tanks)</td>
                            <td><input type="text" v-model="activeForm.shoreTanks"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Loading / Discharging (Ship's Tanks)</td>
                            <td><input type="text" v-model="activeForm.shipTanks"
                                    :disabled="isViewOnly || pageLoading" /></td>
                        </tr>
                        <tr>
                            <td class="field-label">Loading / Discharging Time Start / Stop</td>
                            <td>
                                <div class="inline-grid three-cols">
                                    <div class="mini-field">
                                        <el-date-picker v-model="activeForm.loadingTimeStart" type="datetime"
                                            format="dd/MM/yyyy HH:mm:ss" value-format="yyyy-MM-dd HH:mm:ss"
                                            placeholder="Start" :disabled="isViewOnly || pageLoading"
                                            class="el-full-control" />
                                    </div>
                                    <label> - </label>
                                    <div class="mini-field">
                                        <el-date-picker v-model="activeForm.loadingTimeStop" type="datetime"
                                            format="dd/MM/yyyy HH:mm:ss" value-format="yyyy-MM-dd HH:mm:ss"
                                            placeholder="Stop" :disabled="isViewOnly || pageLoading"
                                            class="el-full-control" />
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Estimated Time Completion - ETC</td>
                            <td>
                                <el-time-picker v-model="activeForm.etc" format="HH:mm" value-format="HH:mm"
                                    placeholder="Select time" :disabled="isViewOnly || pageLoading"
                                    class="el-full-control" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">POB (Time)</td>
                            <td>
                                <el-time-picker v-model="activeForm.pobTime" format="HH:mm" value-format="HH:mm"
                                    placeholder="Select time" :disabled="isViewOnly || pageLoading"
                                    class="el-full-control" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">All Clear (Time)</td>
                            <td>
                                <el-time-picker v-model="activeForm.allClearTime" format="HH:mm" value-format="HH:mm"
                                    placeholder="Select time" :disabled="isViewOnly || pageLoading"
                                    class="el-full-control" />
                            </td>
                        </tr>
                    </table>

                    <div class="subsection-header">Remarks</div>
                    <div class="full-textarea-block">
                        <textarea v-model="activeForm.remarks" rows="5"
                            :disabled="isViewOnly || pageLoading"></textarea>
                    </div>

                    <div class="subsection-header">Items to follow-up</div>
                    <div class="full-textarea-block">
                        <textarea v-model="activeForm.followUpItems" rows="4"
                            :disabled="isViewOnly || pageLoading"></textarea>
                    </div>

                    <div class="subsection-header">Loading Master Information</div>
                    <table class="form-table">
                        <tr>
                            <td class="field-label">Name / Signature</td>
                            <td><input type="text" v-model="activeForm.loadingMasterSignature"
                                    :disabled="isViewOnly || pageLoading" />
                            </td>
                        </tr>
                        <tr>
                            <td class="field-label">Date / Time</td>
                            <td>
                                <el-date-picker v-model="activeForm.loadingMasterDateTime" type="datetime"
                                    format="dd/MM/yyyy HH:mm:ss" value-format="yyyy-MM-dd HH:mm:ss"
                                    placeholder="Select date and time" :disabled="isViewOnly || pageLoading"
                                    class="el-full-control" />
                            </td>
                        </tr>
                    </table>

                    <div class="form-footer-note">Jetty Log Rev. 02</div>
                </div>
            </div>

            <div v-if="showTimeline"
                :class="['timeline-panel', timelineLoading && !timelineRows.length ? 'loading' : '']">
                <div class="timeline-head flex justify-between">
                    <div>
                        Timeline
                        <span class="pl-1 timeline-head-count">({{ timelineRows.length }}/{{ timelinePagination.total
                            }})</span>
                    </div>
                    <div>
                        <i v-if="timelineLoading" class="el-icon-refresh rotating"></i>
                    </div>
                </div>

                <div class="timeline-content-wrap" ref="timelineScroll" @scroll="handleTimelineScroll">
                    <el-timeline v-loading="timelineLoading && !timelineRows.length">
                        <el-timeline-item v-for="(row, index) in timelineRows" :key="row.id"
                            :class="{ lastItem: index === timelineRows.length - 1 }">
                            <div>
                                <span class="timeline-main-title">
                                    {{ getLogTypeInfo(row.logType).label }}
                                </span>

                                <p class="timestamp">
                                    <span>
                                        {{ formatDateTime(row.createTime) }}
                                        <span style="color: #a1a6b5" v-show="row.createBy"> by</span>
                                        {{ row.createBy || '' }}
                                    </span>
                                </p>
                            </div>

                            <el-divider v-show="getVisibleSubs(row).length"></el-divider>

                            <div>
                                <span v-for="sub in getVisibleSubs(row)" :key="sub.id">
                                    <p class="actionDev text-xs">
                                        <span style="color: #a1a6b5">{{ formatFieldName(sub.fieldName) }}</span> :
                                        <span v-if="hasDisplayValue(sub.oldValue)"> Changed from </span>
                                        <span v-if="hasDisplayValue(sub.oldValue)" class="primaryColor">
                                            {{ formatTimelineValue(sub.oldValue) }}
                                        </span>
                                        <span v-if="hasDisplayValue(sub.oldValue)"> to </span>
                                        <span class="primaryColor">
                                            {{ formatTimelineValue(sub.targetValue) }}
                                        </span>
                                    </p>
                                </span>
                            </div>
                        </el-timeline-item>

                        <div class="py-5 text-center">
                            <i v-if="timelineLoading && timelineRows.length" class="el-icon-refresh rotating"></i>
                            <span v-else-if="!timelineFinished && timelineRows.length"
                                class="cursor-pointer text-xs primaryColor" @click="loadMoreTimeline">
                                Load More
                            </span>
                            <span v-else-if="timelineFinished && timelineRows.length" class="text-xs timeline-end-text">
                                No more records
                            </span>
                        </div>
                    </el-timeline>
                </div>
            </div>
        </div>

        <el-dialog title="Photo Preview" :visible.sync="photoDialogVisible" width="70%" top="5vh"
            custom-class="photo-preview-dialog" append-to-body>
            <div class="photo-preview-wrap">
                <img v-if="previewPhotoUrl" :src="previewPhotoUrl" alt="Jetty Photo" class="photo-preview-image" />
            </div>
        </el-dialog>
    </div>
</template>

<script>
import 'primeicons/primeicons.css'
import { singleleUploadFile } from '@/services';
import imageCompression from 'browser-image-compression';
import { createJettyChecklist, deleteChecklistEntry, getChecklistDetail, getChecklistTimeline, getJettyCheckListStatuses, getNominationList, getTeamList, updateChecklistDetail } from '@/services/user';
import { mapState } from 'vuex';

export default {
    name: 'JettyForm',
    data() {
        return {
            pageLoading: false,
            showTimeline: true,
            saving: false,
            uploadingOverviewPictures: false,
            uploadingFaultyPictures: false,
            uploadingBypassPictures: false,
            photoDialogVisible: false,
            previewPhotoUrl: '',
            timelineLoading: false,
            timelineFinished: false,
            timelinePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            timelineRows: [],

            activeEntryIndex: 0,
            entries: [],
            isCreated: false,
            nominationOptions: [],
            teamOptions: [],
            validationErrors: {
                bypassSeal: false,
                hoseNo: false,
                sealRemarks: false,
                sealNumber: false,
                timeTakenOver: false,
                jettyOverviewPhoto: false,
            },
        }
    },
    watch: {
        'activeForm.bypassSeal'(val) {
            if (!['Break Seal', 'Lock Seal'].includes(val)) {
                this.activeForm.sealRemarks = ''
                this.activeForm.sealNumber = ''
            }
        }
    },
    computed: {
        ...mapState(["userInfo"]),
        hasBypassSealPhotos() {
            return this.getPicturesByType(3).length > 0
        },

        activeEntry() {
            return this.entries[this.activeEntryIndex] || null
        },

        activeForm() {
            return this.activeEntry ? this.activeEntry.form : {}
        },

        activeConditionItems() {
            return this.activeEntry ? this.activeEntry.conditionItems : []
        },

        activePictures() {
            return this.activeEntry ? this.activeEntry.pictures : []
        },

        allConditionChecked() {
            return this.activeConditionItems
                .filter(item => item.type !== 'select')
                .every(item => item.checked)
        },
        jettyNo() {
            return this.$route.query.jettyNo || ''
        },
        shiftType() {
            return this.$route.query.shiftType || 'DAY'
        },
        shiftLabel() {
            return this.shiftType === 'NIGHT' ? 'Night' : 'Day'
        },
        selectedDate() {
            return this.$route.query.date || new Date().toISOString().split('T')[0]
        },
        isViewOnly() {
            // Logistics View (16) and Loss Control (17) can never fill the checklist — always view-only.
            const role = this.$store.state.userInfo.role
            return this.$route.query.viewOnly === '1' || role == '16' || role == '17'
        },
        displayDate() {
            const date = new Date(this.selectedDate)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return day + '/' + month + '/' + year
        },
        checklistId() {
            return this.$route.query.id || ''
        },
        submittedCount() {
            return this.timelinePagination.total
        },
        entryIds() {
            try {
                const raw = this.$route.query.ids
                if (!raw) return []
                const parsed = JSON.parse(raw)
                return Array.isArray(parsed) ? parsed : []
            } catch (e) {
                return []
            }
        },
        isAnyUploadingPictures() {
            return (
                this.uploadingOverviewPictures ||
                this.uploadingFaultyPictures ||
                this.uploadingBypassPictures
            )
        },

        showSealFields() {
            return ['Break Seal', 'Lock Seal'].includes(this.activeForm.bypassSeal)
        },
    },
    async mounted() {
        this.pageLoading = true

        try {
            await this.loadTeams()
            await this.loadNominations()

            if (this.entryIds.length) {
                await this.loadEntriesByIds(this.entryIds)
                this.resetAndLoadTimeline()
                return
            }

            this.entries = [this.createDefaultEntry()]

            if (this.$store.state.userInfo.role == '11' || this.$store.state.userInfo.role == '3') {
                this.prefillLoadingMasterInfo()
            }

            if (this.checklistId) {
                await this.getDetail()
                this.resetAndLoadTimeline()
            }
        } finally {
            this.pageLoading = false
        }
    },
    methods: {
        goBack() {
            this.$router.replace({
                path: '/jettyChecklist/index',
                query: { refresh: Date.now() }
            })
        },
        buildEntryPayload(entry, index) {
            const form = entry.form

            return {
                entryNo: index + 1,
                timeTakenOver: form.timeTakenOver,
                teamNo: form.teamNo,
                vesselName: form.vesselName,
                loaFlag: form.loaFlag,
                captainName: form.captainName,
                draftBeforeF: form.draftBeforeF,
                draftBeforeA: form.draftBeforeA,
                draftAfterF: form.draftAfterF,
                draftAfterA: form.draftAfterA,
                freeboardBefore: form.freeboardBefore,
                freeboardAfter: form.freeboardAfter,
                sizeNoManifold: form.manifold,
                rob: form.robTanks,
                mpaWaterVoucherNo: form.mpaVoucherNo,
                totaliserOpeningReading: form.totaliserOpening,
                totaliserClosingReading: form.totaliserClosing,
                loadingDischargingShoreTanks: form.shoreTanks,
                loadingDischargingShipTanks: form.shipTanks,
                loadingDischargingTimeStartStop:
                    form.loadingTimeStart || form.loadingTimeStop
                        ? `${form.loadingTimeStart || ''} - ${form.loadingTimeStop || ''}`
                        : '',
                estimatedTimeCompletion: form.etc,
                pob: form.pobTime,
                allClear: form.allClearTime,
                remarks: form.remarks,
                itemsToFollowUp: form.followUpItems,
                name: form.loadingMasterSignature,
                dateTime: form.loadingMasterDateTime,
                shipsRequestedLoadingRate: form.requestedLoadingRate,
                shipsMaxDischargingRate: form.maxDischargingRate,
                lineDripSample: form.lineDripSample,
                shipsCompositeLoadPortSample: form.compositeSample,
                productsName: form.productName,
                density: form.density,
                temperature: form.temperature,
                shipsNomination: form.shipNomination,
                shoresNomination: form.shoreNomination,
                customer: form.customer,
                surveyor: form.surveyor,
                pilotArrangement: form.pilotArrangement,
                faultyEquipmentRemarks: form.faultyEquipmentRemarks,
                items: entry.conditionItems.map(item => {
                    const reqBody = {
                        checked: item.checked,
                        remark: item.remarks || item.level || ''
                    }

                    if (item.type === 'select') delete reqBody.checked
                    return reqBody
                }),
                pictures: entry.pictures.map(p => ({
                    pictureType: p.pictureType,
                    pictureUrl: p.pictureUrl
                }))
            }
        },

        buildPayload() {
            if (!this.activeEntry) return {}

            const form = this.activeEntry.form

            return {
                checkDate: this.selectedDate,
                shiftType: this.shiftType,
                jettyNo: Number(this.jettyNo),

                timeTakenOver: form.timeTakenOver || '',
                teamNo: form.teamNo || '',
                vesselName: form.vesselName || '',
                loaFlag: form.loaFlag || '',
                captainName: form.captainName || '',

                draftBeforeF: form.draftBeforeF || '',
                draftBeforeA: form.draftBeforeA || '',
                draftAfterF: form.draftAfterF || '',
                draftAfterA: form.draftAfterA || '',

                freeboardBefore: form.freeboardBefore || '',
                freeboardAfter: form.freeboardAfter || '',

                sizeNoManifold: form.manifold || '',
                rob: form.robTanks || '',
                mpaWaterVoucherNo: form.mpaVoucherNo || '',
                totaliserOpeningReading: form.totaliserOpening || '',
                totaliserClosingReading: form.totaliserClosing || '',

                loadingDischargingShoreTanks: form.shoreTanks || '',
                loadingDischargingShipTanks: form.shipTanks || '',
                loadingDischargingTimeStartStop:
                    form.loadingTimeStart || form.loadingTimeStop
                        ? `${form.loadingTimeStart || ''} - ${form.loadingTimeStop || ''}`
                        : '',
                estimatedTimeCompletion: form.etc || '',
                pob: form.pobTime || '',
                allClear: form.allClearTime || '',
                remarks: form.remarks || '',
                itemsToFollowUp: form.followUpItems || '',

                name: form.loadingMasterSignature || '',
                dateTime: form.loadingMasterDateTime || '',

                shipsRequestedLoadingRate: form.requestedLoadingRate || '',
                shipsMaxDischargingRate: form.maxDischargingRate || '',
                lineDripSample: form.lineDripSample || '',
                shipsCompositeLoadPortSample: form.compositeSample || '',
                productsName: form.productName || '',
                density: form.density || '',
                temperature: form.temperature || '',
                shipsNomination: form.shipNomination || '',
                shoresNomination: form.shoreNomination || '',
                customer: form.customer || '',
                surveyor: form.surveyor || '',
                pilotArrangement: form.pilotArrangement || '',

                bypassSeal: form.bypassSeal || '',
                hoseNo: form.hoseNo || '',
                sealRemarks: form.sealRemarks || '',
                sealNumber: form.sealNumber || '',
                nominationId: form.nominationId || null,
                faultyEquipmentRemarks: form.faultyEquipmentRemarks || '',

                items: this.activeEntry.conditionItems.map(item => {
                    const row = {
                        remark: item.remarks || item.level || ''
                    }

                    if (item.type !== 'select') {
                        row.checked = !!item.checked
                    }

                    return row
                }),

                pictures: this.activeEntry.pictures.map(picture => ({
                    pictureType: picture.pictureType,
                    pictureUrl: picture.pictureUrl
                }))
            }
        },
        isUploadingByType(type) {
            if (type === 1) return this.uploadingOverviewPictures
            if (type === 2) return this.uploadingFaultyPictures
            if (type === 3) return this.uploadingBypassPictures
            return false
        },

        setUploadingByType(type, value) {
            if (type === 1) {
                this.uploadingOverviewPictures = value
                return
            }

            if (type === 2) {
                this.uploadingFaultyPictures = value
                return
            }

            if (type === 3) {
                this.uploadingBypassPictures = value
            }
        },

        async handleSave() {
            this.validationErrors.timeTakenOver = false
            this.validationErrors.jettyOverviewPhoto = false

            const overviewPhotos = this.getPicturesByType(1)
            if (!overviewPhotos.length || !this.activeForm.timeTakenOver) {
                if (!this.activeForm.timeTakenOver) {
                    this.$message && this.$message.error('Time Taken Over is required')
                    this.validationErrors.timeTakenOver = true

                }
                if (!overviewPhotos.length) {
                    setTimeout(() => {
                        this.$message && this.$message.error('Jetty Overview Photo is required')
                    }, 100)
                    this.validationErrors.jettyOverviewPhoto = true
                }
                return false
            }

            if (overviewPhotos.length > 2) {
                this.$message && this.$message.error('Jetty Overview Photo allows maximum 2 images')
                return false
            }


            const faultyPhotos = this.getPicturesByType(2)
            if (faultyPhotos.length > 5) {
                this.$message && this.$message.error('Faulty Equipment Photo allows maximum 5 images')
                return false
            }

            const bypassPhotos = this.getPicturesByType(3)
            if (bypassPhotos.length > 2) {
                this.$message && this.$message.error('Bypass Seal Photo allows maximum 2 images')
                return false
            }

            if (!this.validateSealFields()) {
                this.$message && this.$message.error('Please fill all required seal fields')
                return false
            }


            if (this.isViewOnly || this.isAnyUploadingPictures || !this.activeEntry) return false

            this.saving = true

            try {
                const payload = this.buildPayload()
                const entry = this.activeEntry
                const isNew = !entry.id

                let res

                if (isNew) {
                    res = await createJettyChecklist(payload)

                    if (res.code === 200) {
                        if (Array.isArray(res.data)) {
                            const updatedItem = res.data.find(
                                item => Number(item.entryNo) === Number(entry.entryNo)
                            ) || res.data[0]

                            if (updatedItem && updatedItem.id) {
                                this.$set(this.entries[this.activeEntryIndex], 'id', updatedItem.id)
                                this.$set(this.entries[this.activeEntryIndex], 'entryNo', updatedItem.entryNo)
                            }
                        }

                        this.syncEntryIdsToRoute()
                        this.$message && this.$message.success('Updated successfully')
                    } else {
                        return false
                    }
                } else {
                    res = await updateChecklistDetail(entry.id, payload)

                    if (res.code === 200) {
                        this.syncEntryIdsToRoute()
                        this.$message && this.$message.success('Updated successfully')
                    } else {
                        return false
                    }
                }

                await this.reloadActiveEntry()
                this.$set(this.entries[this.activeEntryIndex], 'isDirty', false)
                this.resetAndLoadTimeline()
                return true
            } catch (error) {
                console.error('saveChecklist error:', error)
                this.$message && this.$message.error('Failed to save form')
                return false
            } finally {
                this.saving = false
            }
        },

        async reloadActiveEntry() {
            if (!this.activeEntry || !this.activeEntry.id) return

            try {
                const res = await getChecklistDetail(this.activeEntry.id)
                const detail = res.data

                const updatedEntry = this.mapDetailToEntry(detail, this.activeEntryIndex)
                this.$set(this.entries, this.activeEntryIndex, updatedEntry)
            } catch (error) {
                console.error('reloadActiveEntry error:', error)
            }
        },

        async loadEntriesByIds(ids) {
            try {
                const requests = ids.map(id => getChecklistDetail(id))
                const responses = await Promise.all(requests)
                const details = responses.map(res => res.data).filter(Boolean)

                this.entries = details.map((detail, index) => this.mapDetailToEntry(detail, index))

                if (!this.entries.length) {
                    this.entries = [this.createDefaultEntry()]
                }

                this.activeEntryIndex = 0

                this.syncEntryIdsToRoute()

                if (this.$store.state.userInfo.role == '11' || this.$store.state.userInfo.role == '3') {
                    this.prefillLoadingMasterInfo()
                }
            } catch (error) {
                console.error('loadEntriesByIds error:', error)
                this.$message && this.$message.error('Failed to load checklist entries')
                this.entries = [this.createDefaultEntry()]
            }
        },

        openPhotoDialog(url) {
            if (!url) return
            this.previewPhotoUrl = url
            this.photoDialogVisible = true
        },

        getPictureName(url, index) {
            if (!url) return 'Picture ' + (index + 1)

            const parts = url.split('/')
            return parts[parts.length - 1] || 'Picture ' + (index + 1)
        },

        async getDetail() {
            try {
                const res = await getChecklistDetail(this.$route.query.id)
                const detail = res.data
                this.entries = [this.mapDetailToEntry(detail)]
                this.activeEntryIndex = 0
            } catch (error) {
                console.error('getChecklistDetail error:', error)
                this.$message && this.$message.error('Failed to load checklist detail')
            }
        },

        mapDetailToEntry(detail, index = 0) {
            const entry = this.createDefaultEntry(detail.entryNo || index + 1)
            entry.id = detail.id || null
            entry.entryNo = detail.entryNo || index + 1
            entry.localId = detail.id || (Date.now() + index)
            entry.form.timeTakenOver = detail.timeTakenOver || ''
            entry.form.teamNo = detail.teamNo || ''
            entry.form.vesselName = detail.vesselName || ''
            entry.form.loaFlag = detail.loaFlag || ''
            entry.form.captainName = detail.captainName || ''
            entry.form.draftBeforeF = detail.draftBeforeF || ''
            entry.form.draftBeforeA = detail.draftBeforeA || ''
            entry.form.draftAfterF = detail.draftAfterF || ''
            entry.form.draftAfterA = detail.draftAfterA || ''
            entry.form.freeboardBefore = detail.freeboardBefore || ''
            entry.form.freeboardAfter = detail.freeboardAfter || ''
            entry.form.manifold = detail.sizeNoManifold || ''
            entry.form.robTanks = detail.rob || ''
            entry.form.mpaVoucherNo = detail.mpaWaterVoucherNo || ''
            entry.form.totaliserOpening = detail.totaliserOpeningReading || ''
            entry.form.totaliserClosing = detail.totaliserClosingReading || ''
            entry.form.shoreTanks = detail.loadingDischargingShoreTanks || ''
            entry.form.shipTanks = detail.loadingDischargingShipTanks || ''

            if (detail.loadingDischargingTimeStartStop) {
                const parts = detail.loadingDischargingTimeStartStop.split(' - ')
                entry.form.loadingTimeStart = parts[0] || ''
                entry.form.loadingTimeStop = parts[1] || ''
            }

            entry.form.etc = detail.estimatedTimeCompletion || ''
            entry.form.pobTime = detail.pob || ''
            entry.form.allClearTime = detail.allClear || ''
            entry.form.remarks = detail.remarks || ''
            entry.form.followUpItems = detail.itemsToFollowUp || ''
            entry.form.loadingMasterSignature = detail.name || ''
            entry.form.loadingMasterDateTime = detail.dateTime || ''
            entry.form.requestedLoadingRate = detail.shipsRequestedLoadingRate || ''
            entry.form.maxDischargingRate = detail.shipsMaxDischargingRate || ''
            entry.form.lineDripSample = detail.lineDripSample || ''
            entry.form.compositeSample = detail.shipsCompositeLoadPortSample || ''
            entry.form.productName = detail.productsName || ''
            entry.form.density = detail.density || ''
            entry.form.temperature = detail.temperature || ''
            entry.form.shipNomination = detail.shipsNomination || ''
            entry.form.shoreNomination = detail.shoresNomination || ''
            entry.form.customer = detail.customer || ''
            entry.form.surveyor = detail.surveyor || ''
            entry.form.pilotArrangement = detail.pilotArrangement || ''
            entry.form.bypassSeal = detail.bypassSeal || ''
            entry.form.hoseNo = detail.hoseNo || ''
            entry.form.sealRemarks = detail.sealRemarks || ''
            entry.form.sealNumber = detail.sealNumber || ''
            entry.form.nominationId = detail.nominationId || null
            entry.form.faultyEquipmentRemarks = detail.faultyEquipmentRemarks || ''

            entry.conditionItems = entry.conditionItems.map((item, itemIndex) => {
                const apiItem = (detail.items || [])[itemIndex] || {}

                if (item.type === 'select') {
                    return {
                        ...item,
                        level: apiItem.remark || ''
                    }
                }

                return {
                    ...item,
                    checked: !!apiItem.checked,
                    remarks: apiItem.remark || ''
                }
            })

            entry.pictures = (detail.pictures || []).map(item => ({
                pictureType: item.pictureType,
                pictureUrl: item.pictureUrl
            }))

            return entry
        },

        async getTimeline(isLoadMore = false) {
            if (!this.activeEntry.id || this.timelineLoading || this.timelineFinished) return

            this.timelineLoading = true

            try {
                const params = {
                    pageNum: this.timelinePagination.pageNum,
                    pageSize: this.timelinePagination.pageSize
                }

                const res = await getChecklistTimeline(this.activeEntry.id, params)
                const data = (res && res.data) || {}
                const rows = data.rows || []
                const total = data.total || 0

                this.timelinePagination.total = total

                if (isLoadMore) {
                    this.timelineRows = this.timelineRows.concat(rows)
                } else {
                    this.timelineRows = rows
                }

                if (!rows.length || this.timelineRows.length >= total) {
                    this.timelineFinished = true
                }
            } catch (error) {
                console.error('getChecklistTimeline error:', error)
                this.$message && this.$message.error('Failed to load timeline')
            } finally {
                this.timelineLoading = false
            }
        },

        getLogTypeInfo(logType) {
            const map = {
                1: { label: 'Checklist Created', pill: 'Created', className: 'created' },
                2: { label: 'Checklist Updated', pill: 'Updated', className: 'updated' },
                3: { label: 'Checklist Deleted', pill: 'Deleted', className: 'deleted' }
            }

            return map[logType] || { label: 'Checklist Activity', pill: 'Unknown', className: 'updated' }
        },

        formatDateTime(value) {
            if (!value) return '-'

            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return value

            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            const hour = String(date.getHours()).padStart(2, '0')
            const minute = String(date.getMinutes()).padStart(2, '0')
            const second = String(date.getSeconds()).padStart(2, '0')

            return `${day}/${month}/${year} ${hour}:${minute}:${second}`
        },

        formatFieldName(fieldName) {
            if (!fieldName) return '-'

            if (fieldName.startsWith('items[')) {
                const match = fieldName.match(/^items\[(\d+)\]\.(.+)$/)

                if (match) {
                    const itemIndex = Number(match[1])
                    const prop = match[2]
                    const itemLabel = this.activeConditionItems[itemIndex - 1]?.label || `Item ${itemIndex + 1}`

                    const propMap = {
                        checked: 'Status',
                        remark: 'Remarks',
                        level: 'Level'
                    }

                    return `${itemLabel} ${propMap[prop] || prop}`
                }

                return fieldName
            }

            return fieldName
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\./g, ' ')
                .replace(/^./, str => str.toUpperCase())
        },

        getFieldChangeText(sub) {
            this.isCreated = false
            if (!sub) return '-'

            const field = this.formatFieldName(sub.fieldName)
            const oldValue = sub.oldValue == null || sub.oldValue.trim() === '' ? '-' : sub.oldValue
            const targetValue = sub.targetValue == null || sub.targetValue === '' ? '-' : sub.targetValue

            if (oldValue == '-') {
                this.isCreated = true
                return `${field}: ${targetValue}`
            } else {
                this.isCreated = false
                return `${field}: ${oldValue} → ${targetValue}`
            }
        },

        handleTimelineScroll(event) {
            const el = event.target
            if (!el || this.timelineLoading || this.timelineFinished) return

            const threshold = 80
            const reachedBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - threshold

            if (reachedBottom) {
                this.loadMoreTimeline()
            }
        },

        loadMoreTimeline() {
            if (this.timelineLoading || this.timelineFinished) return

            this.timelinePagination.pageNum += 1
            this.getTimeline(true)
        },

        resetAndLoadTimeline() {
            this.timelinePagination.pageNum = 1
            this.timelinePagination.total = 0
            this.timelineRows = []
            this.timelineFinished = false
            this.getTimeline(false)
        },

        submittedCount() {
            return this.timelinePagination.total
        },

        getVisibleSubs(row) {
            if (!row || !row.subs || !row.subs.length) return []

            return row.subs.filter(sub => {
                if (!sub || !sub.fieldName) return false
                if (sub.fieldName === 'items[5].checked') return false
                return true
            })
        },

        hasDisplayValue(value) {
            return value !== null && value !== undefined && String(value).length > 0
        },

        formatTimelineValue(value) {
            if (value === null || value === undefined || value === '') return 'Empty'
            if (value === 'true') return 'Yes'
            if (value === 'false') return 'No'
            return value
        },

        toggleAllConditionStatus() {
            const shouldCheck = !this.allConditionChecked

            this.activeEntry.conditionItems = this.activeEntry.conditionItems.map(item => {
                if (item.type === 'select') return item

                return {
                    ...item,
                    checked: shouldCheck
                }
            })
        },

        getDefaultLoadingMasterName() {
            return this.$store?.state?.userInfo?.nickName || ''
        },

        getCurrentDateTime() {
            const now = new Date()
            const year = now.getFullYear()
            const month = String(now.getMonth() + 1).padStart(2, '0')
            const day = String(now.getDate()).padStart(2, '0')
            const hour = String(now.getHours()).padStart(2, '0')
            const minute = String(now.getMinutes()).padStart(2, '0')
            const second = String(now.getSeconds()).padStart(2, '0')

            return `${year}-${month}-${day} ${hour}:${minute}:${second}`
        },

        prefillLoadingMasterInfo(force = false) {
            if (force || !this.activeForm.loadingMasterSignature) {
                this.activeForm.loadingMasterSignature = this.getDefaultLoadingMasterName()
            }

            if (force || !this.activeForm.loadingMasterDateTime) {
                this.activeForm.loadingMasterDateTime = this.getCurrentDateTime()
            }
        },

        createDefaultForm() {
            return {
                timeTakenOver: '',
                teamNo: '',
                vesselName: '',
                loaFlag: '',
                captainName: '',
                draftBeforeF: '',
                draftBeforeA: '',
                draftAfterF: '',
                draftAfterA: '',
                freeboardBefore: '',
                freeboardAfter: '',
                manifold: '',
                robTanks: '',
                mpaVoucherNo: '',
                totaliserOpening: '',
                totaliserClosing: '',
                requestedLoadingRate: '',
                maxDischargingRate: '',
                lineDripSample: '',
                compositeSample: '',
                productName: '',
                density: '',
                temperature: '',
                shipNomination: '',
                shoreNomination: '',
                customer: '',
                surveyor: '',
                pilotArrangement: '',
                shoreTanks: '',
                shipTanks: '',
                loadingTimeStart: '',
                loadingTimeStop: '',
                etc: '',
                pobTime: '',
                allClearTime: '',
                remarks: '',
                followUpItems: '',
                loadingMasterSignature: '',
                loadingMasterDateTime: '',
                bypassSeal: '',
                hoseNo: '',
                sealRemarks: '',
                sealNumber: '',
                nominationId: null,
                faultyEquipmentRemarks: '',
            }
        },

        createDefaultConditionItems() {
            return [
                { label: 'Fender', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Crane', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Gangway', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Capstan', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Sump', type: 'select', level: '', remarks: '' },
                { label: 'Sump Pump Level', type: 'select', level: '', remarks: '', options: ['LOW', 'HIGH'] },
                { label: 'Mooring Ropes', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Loading Arm(s)', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Loading Hose(s)', type: 'checkbox', checked: false, remarks: '' },
                { label: 'MPA Water Meter', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Bunker PD Meter', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Recovery Pump', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Lightings', type: 'checkbox', checked: false, remarks: '' },
                { label: 'Housekeeping', type: 'checkbox', checked: false, remarks: '' },
            ]
        },

        createDefaultEntry(entryNo = null) {
            return {
                id: null,
                entryNo,
                localId: Date.now() + Math.random(),
                form: this.createDefaultForm(),
                conditionItems: this.createDefaultConditionItems(),
                pictures: []
            }
        },

        addEntry() {
            const nextEntryNo = this.entries.length
                ? Math.max(...this.entries.map(entry => Number(entry.entryNo) || 0)) + 1
                : 1

            const entry = this.createDefaultEntry(nextEntryNo)

            if (this.$store.state.userInfo.role == '11' || this.$store.state.userInfo.role == '3') {
                entry.form.loadingMasterSignature = this.getDefaultLoadingMasterName()
                entry.form.loadingMasterDateTime = this.getCurrentDateTime()
            }

            this.entries.push(entry)
            this.activeEntryIndex = this.entries.length - 1
        },

        async removeEntry(index) {
            if (this.entries.length === 1) {
                this.$message && this.$message.warning('At least one entry is required')
                return
            }

            const entry = this.entries[index]

            try {
                await this.$confirm(
                    `Delete Entry ${index + 1}?`,
                    'Confirm Deletion',
                    {
                        confirmButtonText: 'Delete',
                        cancelButtonText: 'Cancel',
                        type: 'warning'
                    }
                )

                if (entry.id) {
                    await deleteChecklistEntry(entry.id)
                }

                const wasActiveEntry = index === this.activeEntryIndex

                this.entries.splice(index, 1)
                this.syncEntryIdsToRoute()

                if (this.activeEntryIndex >= this.entries.length) {
                    this.activeEntryIndex = this.entries.length - 1
                }

                this.$nextTick(() => {
                    if (this.activeEntry && this.activeEntry.id) {
                        this.resetAndLoadTimeline()
                    } else {
                        this.timelinePagination.pageNum = 1
                        this.timelinePagination.total = 0
                        this.timelineRows = []
                        this.timelineFinished = false
                    }
                })

                this.$message && this.$message.success('Entry deleted')
            } catch (err) {
                if (err !== 'cancel') {
                    console.error('delete entry error:', err)
                    this.$message && this.$message.error('Failed to delete entry')
                }
            }
        },

        setActiveEntry(index) {
            if (index === this.activeEntryIndex) return

            this.activeEntryIndex = index

            this.$nextTick(() => {
                if (this.activeEntry && this.activeEntry.id) {
                    this.resetAndLoadTimeline()
                } else {
                    this.timelinePagination.pageNum = 1
                    this.timelinePagination.total = 0
                    this.timelineRows = []
                    this.timelineFinished = false
                }
            })
        },

        async loadNominations() {
            try {
                const res = await getNominationList({
                    jettyNo: this.jettyNo
                })

                this.nominationOptions = res.data.rows || []

            } catch (error) {
                console.error('loadNominations error:', error)
                this.nominationOptions = []
            }
        },

        onNominationChange(nominationId) {
            const nomination = this.nominationOptions.find(item => item.id === nominationId)
            if (!nomination) return

            this.applyNominationData(nomination)
        },

        reExtractNomination() {
            if (!this.activeForm.nominationId) {
                this.$message && this.$message.warning('Please select a nomination first')
                return
            }

            const nomination = this.nominationOptions.find(
                item => item.id === this.activeForm.nominationId
            )

            if (!nomination) {
                this.$message && this.$message.warning('Selected nomination not found')
                return
            }

            this.applyNominationData(nomination)
            this.$nextTick(_ => { this.$message.success('Nomination data re-extracted') })
        },

        applyNominationData(nomination) {
            const form = this.activeForm
            if (!form) return
            form.nominationId = nomination.id || null
            form.vesselName = `${nomination.vesselName} (${nomination.bargeName})` || ''
            form.loaFlag = nomination.lengthOverall || ''
            form.totaliserOpening = nomination.readBeforeLoad || ''
            form.totaliserClosing = nomination.readAfterLoad || ''
            form.requestedLoadingRate = nomination.maxDischargeRate || ''
            form.productName = nomination.product || ''
            form.shipNomination = Array.isArray(nomination.shipNominationInfo)
                ? nomination.shipNominationInfo
                    .map(item => item.shipNomination || item.nomination || item.qty || '')
                    .filter(Boolean)
                    .join(', ')
                : ''
            form.shoreNomination = nomination.nominatedQtyMax || ''
            form.customer = nomination.customerCompanyName || form.customer || ''
            form.surveyor = Array.isArray(nomination.slistNomination)
                ? nomination.slistNomination
                    .map(item => item.surveyorName)
                    .filter(Boolean)
                    .join(', ')
                : ''
            form.pilotArrangement = nomination.typeOfPilot || ''
            form.shoreTanks = Array.isArray(nomination.tlistNomination)
                ? nomination.tlistNomination
                    .map(item => item.tankNumber)
                    .filter(Boolean)
                    .join(', ')
                : ''
            form.loadingTimeStart = this.extractDateTime(nomination.startedLoadingDischarging)
            form.loadingTimeStop = this.extractDateTime(nomination.finishedLoadingDischarging)
            form.hoseNo = nomination.hose || ''
        },

        extractDateTime(value) {
            if (!value) return ''

            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return ''

            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hour = String(date.getHours()).padStart(2, '0')
            const minute = String(date.getMinutes()).padStart(2, '0')
            const second = String(date.getSeconds()).padStart(2, '0')

            return `${year}-${month}-${day} ${hour}:${minute}:${second}`
        },

        getPicturesByType(type) {
            if (!this.activeEntry || !Array.isArray(this.activeEntry.pictures)) return []
            return this.activeEntry.pictures.filter(picture => Number(picture.pictureType) === Number(type))
        },

        getPictureLimit(type) {
            const limits = {
                1: 2, // Jetty Overview
                2: 5, // Faulty Equipment
                3: 2  // Bypass Seal
            }

            return limits[type] || 0
        },

        triggerFileInputByType(type) {
            if (this.isViewOnly || this.isUploadingByType(type)) return

            const currentCount = this.getPicturesByType(type).length
            const limit = this.getPictureLimit(type)

            if (currentCount >= limit) {
                this.$message && this.$message.warning(`Maximum ${limit} images allowed`)
                return
            }

            const refMap = {
                1: 'overviewPictureInput',
                2: 'faultyPictureInput',
                3: 'bypassPictureInput'
            }

            const refName = refMap[type]
            this.$refs[refName] && this.$refs[refName].click()
        },

        async handlePictureChangeByType(event, type) {
            const files = Array.from(event.target.files || [])
            if (!files.length || !this.activeEntry) return

            const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1280,
                useWebWorker: true
            }

            this.setUploadingByType(type, true)

            try {
                for (const file of files) {
                    const compressedFile = await imageCompression(file, options)

                    const res = await singleleUploadFile(compressedFile)
                    const uploadedUrl = res.url

                    if (uploadedUrl) {
                        this.activeEntry.pictures.push({
                            pictureType: type,
                            pictureUrl: uploadedUrl
                        })
                    }
                }
                this.$message.success('Photo uploaded successfully')
            } catch (error) {
                console.error(error)
                this.$message.error('Upload failed')
            } finally {
                this.setUploadingByType(type, false)
                event.target.value = ''
            }
        },

        removePictureByType(type, index) {
            if (!this.activeEntry) return

            const picturesOfType = this.getPicturesByType(type)
            const target = picturesOfType[index]
            if (!target) return

            const actualIndex = this.activeEntry.pictures.findIndex(
                picture =>
                    picture.pictureType === target.pictureType &&
                    picture.pictureUrl === target.pictureUrl
            )

            if (actualIndex >= 0) {
                this.activeEntry.pictures.splice(actualIndex, 1)
            }

            if (type === 3 && this.getPicturesByType(3).length === 0) {
                this.activeForm.bypassSeal = ''
            }
        },

        syncEntryIdsToRoute() {
            const ids = this.entries
                .map(entry => entry.id)
                .filter(Boolean)

            const nextIds = ids.length ? JSON.stringify(ids) : undefined
            const currentIds = this.$route.query.ids

            if (currentIds === nextIds) {
                return
            }

            this.$router.replace({
                path: this.$route.path,
                query: {
                    ...this.$route.query,
                    ids: nextIds
                }
            }).catch(err => {
                if (err && err.name !== 'NavigationDuplicated') {
                    throw err
                }
            })
        },

        isSealRequired() {
            return ['Break Seal', 'Lock Seal'].includes(this.activeForm.bypassSeal)
        },

        validateSealFields() {
            const hasBypassPhotos = this.getPicturesByType(3).length > 0

            this.validationErrors.bypassSeal = false
            this.validationErrors.hoseNo = false
            this.validationErrors.sealRemarks = false
            this.validationErrors.sealNumber = false

            if (!hasBypassPhotos) {
                return true
            }

            this.validationErrors.bypassSeal = !this.activeForm.bypassSeal

            if (!this.activeForm.bypassSeal) {
                return false
            }

            if (!this.isSealRequired()) {
                return true
            }

            this.validationErrors.hoseNo = !this.activeForm.hoseNo?.trim()
            this.validationErrors.sealRemarks = !this.activeForm.sealRemarks?.trim()
            this.validationErrors.sealNumber = !this.activeForm.sealNumber?.trim()

            return !(
                this.validationErrors.bypassSeal ||
                this.validationErrors.hoseNo ||
                this.validationErrors.sealRemarks ||
                this.validationErrors.sealNumber
            )
        },

        getPreviousShiftInfo() {
            if (this.shiftType === 'DAY') {
                const date = new Date(this.selectedDate)
                date.setDate(date.getDate() - 1)

                return {
                    date: date.toISOString().split('T')[0],
                    shiftType: 'NIGHT'
                }
            }

            return {
                date: this.selectedDate,
                shiftType: 'DAY'
            }
        },

        getLastEntry(entries) {
            if (!Array.isArray(entries) || !entries.length) return null

            return [...entries].sort((a, b) => {
                return Number(a.entryNo || 0) - Number(b.entryNo || 0)
            })[entries.length - 1]
        },

        async retrievePreviousShiftLastEntry() {
            try {
                const previous = this.getPreviousShiftInfo()

                const res = await getJettyCheckListStatuses({
                    date: previous.date
                })

                const payload = res.data || {}
                const shiftMap = previous.shiftType === 'DAY'
                    ? payload.day || {}
                    : payload.night || {}

                const previousEntries = shiftMap[String(this.jettyNo)] || []
                const lastEntry = this.getLastEntry(previousEntries)

                if (!lastEntry || !lastEntry.id) {
                    this.$message && this.$message.warning('No previous shift entry found')
                    return
                }

                const detailRes = await getChecklistDetail(lastEntry.id)
                const detail = detailRes.data

                const nextEntryNo = this.entries.length
                    ? Math.max(...this.entries.map(entry => Number(entry.entryNo) || 0)) + 1
                    : 1

                const newEntry = this.mapDetailToEntry(detail, this.entries.length)

                newEntry.form.loadingMasterSignature = this.getDefaultLoadingMasterName()
                newEntry.form.loadingMasterDateTime = this.getCurrentDateTime()

                newEntry.id = null
                newEntry.entryNo = nextEntryNo
                newEntry.localId = Date.now() + Math.random()

                if (
                    this.entries.length === 1 &&
                    !this.entries[0].id
                ) {
                    newEntry.entryNo = 1
                    this.$set(this.entries, 0, newEntry)
                    this.activeEntryIndex = 0
                } else {
                    const nextEntryNo = this.entries.length
                        ? Math.max(...this.entries.map(entry => Number(entry.entryNo) || 0)) + 1
                        : 1

                    newEntry.entryNo = nextEntryNo
                    this.entries.push(newEntry)
                    this.activeEntryIndex = this.entries.length - 1
                }

                this.$message && this.$message.success('Previous shift last entry retrieved')
            } catch (error) {
                console.error('retrievePreviousShiftLastEntry error:', error)
                this.$message && this.$message.error('Failed to retrieve previous shift entry')
            }
        },
        async loadTeams() {
            try {
                const res = await getTeamList()

                this.teamOptions = (res.data?.rows || res.rows || [])
                    .filter(item => item.status === '0' && item.delFlag === '0')
                    .map(item => ({
                        label: item.teamName,
                        value: item.teamName,
                        teamId: item.teamId
                    }))
            } catch (error) {
                console.error('loadTeams error:', error)
                this.teamOptions = []
            }
        },
    }
}
</script>

<style scoped lang="scss">
.jetty-form-page {
    max-height: 90vh;
}

.top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.top-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.shift-chip {
    min-width: 110px;
    height: 40px;
    padding: 0 16px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
}

.shift-chip.day,
.shift-chip.night {
    background: #1188db;
}

.jetty-label {
    font-size: 16px;
    font-weight: 700;
    color: #2f3542;
}

.top-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ghost-btn,
.primary-btn,
.secondary-btn {
    height: 42px;
    padding: 0 18px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.ghost-btn {
    border: 1px solid #96c0e4;
    background: #dff0ff;
    color: #1685da;
}

.primary-btn {
    border: none;
    background: #108add;
    color: #fff;
}

.primary-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.secondary-btn {
    border: none;
    background: #e7f0f8;
    color: #1685da;
}

.page-body {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 18px;
    align-items: start;
}

.page-body.timeline-hidden {
    grid-template-columns: 1fr;
}

.form-panel,
.timeline-panel {
    background: #fff;
    border-radius: 18px;
    box-shadow: 0 8px 20px rgba(20, 37, 63, 0.12);
    overflow: hidden;
}

.form-scroll {
    max-height: 80vh;
    overflow: auto;
    padding: 16px;
}

.date-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 16px 16px;
    border-bottom: 1px solid #e7edf3;
    justify-content: space-between;
}

.date-row label {
    font-size: 14px;
    font-weight: 700;
    color: #3d4350;
}

.top-date-input {
    width: 170px;
    height: 38px;
    border: 1px solid #cfd6e0;
    border-radius: 6px;
    padding: 0 12px;
    font-size: 14px;
    color: #414854;
    background: #fff;
}

.section-title {
    height: 58px;
    background: #dff0ff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    color: #1d6fb0;
    font-size: 17px;
    font-weight: 700;
    margin: 16px;
    border-radius: 12px 12px 0 0;
}

.section-bar {
    width: 5px;
    height: 24px;
    border-radius: 999px;
    background: #1a8ad8;
}

.subsection-header {
    width: calc(100% - 32px);
    margin: 18px 16px 0;
    padding: 10px 0;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    color: #3e4754;
    border-top: 1px solid #e0e6ed;
    border-bottom: 1px solid #e0e6ed;
}

.form-table {
    width: calc(100% - 32px);
    margin: 0 16px 16px;
    border-collapse: collapse;
}

.form-table td {
    border: 1px solid #e0e6ed;
    vertical-align: middle;
    padding: 8px 10px;
}

.table-head-row td {
    font-size: 11px;
    font-weight: 700;
    color: #4a5564;
    background: #fafbfd;
}

.field-label {
    width: 220px;
    font-size: 12px;
    font-weight: 700;
    color: #3f4552;
}

.status-col {
    width: 120px;
}

.form-table input,
.form-table select,
.form-table textarea {
    width: 100%;
    border: 1px solid #cfd6e0;
    border-radius: 5px;
    padding: 0 10px;
    font-size: 12px;
    color: #3f4552;
    background: #fff;
}

.form-table input,
.form-table select {
    height: 32px;
}

.form-table textarea {
    padding-top: 10px;
    min-height: 90px;
    resize: vertical;
}

.form-table input[type="checkbox"] {
    width: 12px;
    height: 12px;
    transform: scale(0.9);
    margin: 0;
}

.form-table input[type="radio"] {
    width: 12px;
    height: 12px;
    transform: scale(0.9);
    margin: 0 4px 0 0;
}

.inline-grid {
    display: grid;
    gap: 10px;
}

.inline-grid.two-cols {
    grid-template-columns: 1fr 1fr;
}

.inline-grid.three-cols {
    grid-template-columns: 2fr 1fr 2fr;
    align-items: center;
    justify-content: space-between;
    text-align: center;
}

.inline-grid.one-col {
    grid-template-columns: 1fr;
}

.mini-field {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mini-field label {
    font-size: 12px;
    font-weight: 700;
    color: #3f4552;
}

.mini-note {
    margin-top: 6px;
    font-size: 10px;
    color: #8994a8;
    font-weight: 600;
}

.upload-box {
    min-height: 72px;
    border: 1px dashed #b7d6f3;
    border-radius: 8px;
    color: #1788db;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
}

.upload-box i {
    font-size: 14px;
}

.radio-row {
    display: flex;
    gap: 18px;
    align-items: center;
    min-height: 32px;
    font-size: 12px;
}

.radio-row label {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #3f4552;
}

.full-textarea-block {
    width: calc(100% - 32px);
    margin: 0 16px 16px;
}

.full-textarea-block textarea {
    width: 100%;
    border: 1px solid #cfd6e0;
    border-radius: 6px;
    padding: 10px;
    font-size: 12px;
    color: #3f4552;
    resize: vertical;
}

.form-footer-note {
    text-align: right;
    font-size: 10px;
    color: #98a2b3;
    font-weight: 600;
    padding: 0 16px 16px;
}

.hidden-file-input {
    display: none;
}

.uploaded-picture-list {
    width: calc(100% - 32px);
    margin: 8px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.uploaded-picture-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 10px;
    border: 1px solid #dbe4ee;
    border-radius: 6px;
    background: #fafcff;
    font-size: 12px;
}

.uploaded-picture-left {
    min-width: 0;
    flex: 1;
}

.picture-link-btn {
    border: none;
    background: transparent;
    padding: 0;
    color: #1685da;
    cursor: pointer;
    text-align: left;
    word-break: break-all;
    font-size: 12px;
}

.picture-link-btn:hover {
    text-decoration: underline;
}

.uploaded-picture-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.uploaded-picture-actions a {
    color: #1685da;
    text-decoration: none;
    font-size: 12px;
}

.remove-picture-btn {
    border: none;
    background: #ffe9e9;
    color: #c43d3d;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.photo-preview-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
}

.photo-preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 8px;
}

.form-table select {
    height: 32px;
    border: 1px solid #cfd6e0;
    border-radius: 6px;
    padding: 0 40px 0 12px;
    font-size: 14px;
    color: #161b26;
    background-color: #fff;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b97aa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 12px;
}

.timeline-status-pill.created {
    background: #dbf5e9;
    color: #1aab6f;
}

.timeline-status-pill.updated {
    background: #dff0ff;
    color: #1685da;
}

.timeline-status-pill.deleted {
    background: #ffe9e9;
    color: #c43d3d;
}

.timeline-loading-more,
.timeline-end-text {
    text-align: center;
    padding: 14px 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #8392a8;
}

.form-table input:disabled,
.form-table select:disabled,
.form-table textarea:disabled,
.date-row input:disabled,
.full-textarea-block textarea:disabled {
    background: #f5f7fa;
    color: #7b8794;
    cursor: not-allowed;
    opacity: 1;
}

.upload-box.disabled {
    background: #f5f7fa;
    border-color: #d7dee8;
    color: #9aa6b2;
    cursor: not-allowed;
}

.timeline-panel {
    background: #ffffff;
    box-shadow: 0px 4px 4px 1px rgba(196, 196, 196, 0.1);
    border-radius: 18px;
    overflow: hidden;
    height: 80vh;
}

.timeline-head {
    font-weight: 700;
    padding: 15px 22px;
    border-bottom: 4px solid #fafafa;
    color: #4c565c;
    font-size: 15px;
}

.timeline-head-count {
    font-size: 10px;
}

.timeline-content-wrap {
    padding: 24px 20px;
    height: calc(80vh - 58px);
    overflow-y: auto;
}

.timeline-main-title {
    font-weight: 700;
    color: #1d222d;
    margin-right: 8px;
}

.timeline-main-status {
    color: #1685da;
    font-weight: 600;
}

.timestamp {
    font-size: 12px;
    color: #82889c;
    margin-top: 6px;
}

.actionDev {
    color: #82889c;
    margin: 6px 0;
    line-height: 1.5;
}

.primaryColor {
    color: #1685da;
}

.lastItem ::v-deep .el-timeline-item__tail {
    display: none;
}

.loading .timeline-content-wrap {
    overflow: hidden;
}

::v-deep .el-timeline-item__node {
    background: #1188db;
}

::v-deep .el-timeline-item__tail {
    border-color: #1188db;
}

::v-deep .el-timeline-item__content {
    font-size: 14px;
    color: #82889c;
}

::v-deep .el-divider--horizontal {
    margin: 8px 0;
    width: 100%;
}

.rotating {
    animation: rotating 1s linear infinite;
}

@keyframes rotating {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.top-date-picker {
    width: 170px;
}

.el-full-control {
    width: 100%;
}

::v-deep(.el-date-editor.el-input),
::v-deep(.el-date-editor.el-input__wrapper),
::v-deep(.el-date-editor.el-input.el-tooltip__trigger),
::v-deep(.el-date-editor.el-input__wrapper.el-tooltip__trigger),
::v-deep(.el-time-editor.el-input),
::v-deep(.el-time-editor.el-input__wrapper),
::v-deep(.el-time-editor.el-input.el-tooltip__trigger),
::v-deep(.el-time-editor.el-input__wrapper.el-tooltip__trigger) {
    width: 100%;
}

::v-deep(.el-input__wrapper) {
    min-height: 32px;
    border-radius: 6px;
    box-shadow: 0 0 0 1px #cfd6e0 inset !important;
    background: #fff;
}

::v-deep(.el-input__inner) {
    font-size: 12px;
    color: #3f4552;
}

::v-deep(.el-input.is-disabled .el-input__wrapper),
::v-deep(.el-date-editor.is-disabled .el-input__wrapper),
::v-deep(.el-time-editor.is-disabled .el-input__wrapper) {
    background: #f5f7fa;
    color: #7b8794;
    box-shadow: 0 0 0 1px #d7dee8 inset !important;
    cursor: not-allowed;
}

::v-deep(.el-input.is-disabled .el-input__inner),
::v-deep(.el-date-editor.is-disabled .el-input__inner),
::v-deep(.el-time-editor.is-disabled .el-input__inner) {
    color: #7b8794;
    -webkit-text-fill-color: #7b8794;
}

.subsection-header-action {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.check-all-btn {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 28px;
    padding: 0 12px;
    border: 1px solid #96c0e4;
    border-radius: 8px;
    background: #dff0ff;
    color: #1685da;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.check-all-btn:hover {
    background: #cfe8ff;
}

.entry-tabs-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid #e7edf3;
}

.entry-tabs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.entry-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 3px solid transparent;
    background: #fff;
    color: #5a677a;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.entry-tab.active {
    background: #eef7ff;
    color: #108add;
    border-bottom-color: #108add;
}

.remove-entry-btn {
    border: none;
    background: transparent;
    color: #e53935;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    line-height: 1;
}

.add-entry-btn {
    height: 42px;
    padding: 0 18px;
    border: 1px dashed #108add;
    border-radius: 12px;
    background: #fff;
    color: #108add;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.nomination-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.field-label.required::before {
    content: '* ';
    color: red;
}

.error-border {
    border: 1px solid red !important;
    border-radius: 4px !important;
}
</style>